import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiFileText, FiSearch, FiFilter, FiExternalLink, FiUser,
  FiCalendar, FiBookOpen, FiMail, FiX, FiCheck, FiClock,
  FiGrid, FiList, FiChevronDown, FiMessageSquare, FiSend,
  FiLink, FiDownload, FiInfo, FiCheckCircle, FiXCircle
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import './JurnalDosen.css';

const JurnalDosen = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [journals, setJournals] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 12, total_pages: 1 });
  const [filters, setFilters] = useState({ years: [], dosen: [], kategori: {} });
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKategori, setSelectedKategori] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedDosen, setSelectedDosen] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedJournal, setSelectedJournal] = useState(null);
  
  // Request form states
  const [requestForm, setRequestForm] = useState({
    keperluan: 'Skripsi',
    catatan: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // My requests
  const [myRequests, setMyRequests] = useState([]);
  const [showMyRequests, setShowMyRequests] = useState(false);

  const fetchJournals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        mahasiswa_id: user?.id || ''
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (selectedKategori) params.append('kategori', selectedKategori);
      if (selectedYear) params.append('tahun', selectedYear);
      if (selectedDosen) params.append('dosen_id', selectedDosen);
      
      const response = await fetch(`/api/jurnal_dosen.php?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setJournals(data.data || []);
        setPagination(prev => ({ ...prev, ...data.pagination }));
        setFilters(data.filters || { years: [], dosen: [], kategori: {} });
      }
    } catch (error) {
      console.error('Error fetching journals:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchQuery, selectedKategori, selectedYear, selectedDosen, user?.id]);

  const fetchMyRequests = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(`/api/user/request_sitasi_jurnal.php?action=mahasiswa&user_id=${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        setMyRequests(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching my requests:', error);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchJournals();
  }, [fetchJournals]);

  useEffect(() => {
    fetchMyRequests();
  }, [fetchMyRequests]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!selectedJournal || !user?.id) return;
    
    setSubmitting(true);
    try {
      const response = await fetch('/api/user/request_sitasi_jurnal.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_jurnal: selectedJournal.id_jurnal,
          id_mahasiswa: user.id,
          keperluan: requestForm.keperluan,
          catatan: requestForm.catatan
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: 'success', text: 'Request sitasi berhasil diajukan!' });
        setShowRequestModal(false);
        setRequestForm({ keperluan: 'Skripsi', catatan: '' });
        fetchJournals();
        fetchMyRequests();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Gagal mengajukan request sitasi' });
    } finally {
      setSubmitting(false);
    }
  };

  const openRequestModal = (journal) => {
    setSelectedJournal(journal);
    setShowRequestModal(true);
    setMessage({ type: '', text: '' });
  };

  const openDetailModal = (journal) => {
    setSelectedJournal(journal);
    setShowDetailModal(true);
  };

  const getKategoriLabel = (kategori) => {
    const labels = {
      'keperawatan': 'Keperawatan',
      'kesehatan_masyarakat': 'Kesehatan Masyarakat',
      'profesi_ners': 'Profesi Ners',
      'umum': 'Umum'
    };
    return labels[kategori] || kategori;
  };

  const getStatusBadge = (journal) => {
    if (journal.has_pending_request > 0) {
      return <span className="jurnal-dosen__badge jurnal-dosen__badge--pending"><FiClock /> Menunggu</span>;
    }
    if (journal.last_request_status === 'approved') {
      return <span className="jurnal-dosen__badge jurnal-dosen__badge--approved"><FiCheck /> Disetujui</span>;
    }
    if (journal.last_request_status === 'rejected') {
      return <span className="jurnal-dosen__badge jurnal-dosen__badge--rejected"><FiX /> Ditolak</span>;
    }
    return null;
  };

  const canRequest = (journal) => {
    return !journal.has_pending_request && journal.last_request_status !== 'approved';
  };

  return (
    <div className="jurnal-dosen">
      {/* Header */}
      <div className="jurnal-dosen__header">
        <div className="jurnal-dosen__header-content">
          <h1><FiFileText /> Jurnal Dosen</h1>
          <p>Lihat dan ajukan request sitasi jurnal dari dosen STIKBAR</p>
        </div>
        <button 
          className="jurnal-dosen__my-requests-btn"
          onClick={() => setShowMyRequests(true)}
        >
          <FiSend /> Request Saya
          {myRequests.filter(r => r.status === 'pending').length > 0 && (
            <span className="jurnal-dosen__badge-count">
              {myRequests.filter(r => r.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`jurnal-dosen__message jurnal-dosen__message--${message.type}`}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiXCircle />}
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })}><FiX /></button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="jurnal-dosen__toolbar">
        <form className="jurnal-dosen__search" onSubmit={handleSearch}>
          <FiSearch />
          <input 
            type="text"
            placeholder="Cari judul, penulis, atau nama jurnal..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>
        
        <div className="jurnal-dosen__toolbar-actions">
          <button 
            className={`jurnal-dosen__filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter <FiChevronDown />
          </button>
          
          <div className="jurnal-dosen__view-toggle">
            <button 
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button 
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="jurnal-dosen__filters">
          <div className="jurnal-dosen__filter-group">
            <label>Kategori</label>
            <select value={selectedKategori} onChange={(e) => setSelectedKategori(e.target.value)}>
              <option value="">Semua Kategori</option>
              {Object.keys(filters.kategori).map(kat => (
                <option key={kat} value={kat}>
                  {getKategoriLabel(kat)} ({filters.kategori[kat]})
                </option>
              ))}
            </select>
          </div>
          
          <div className="jurnal-dosen__filter-group">
            <label>Tahun</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
              <option value="">Semua Tahun</option>
              {filters.years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          
          <div className="jurnal-dosen__filter-group">
            <label>Dosen</label>
            <select value={selectedDosen} onChange={(e) => setSelectedDosen(e.target.value)}>
              <option value="">Semua Dosen</option>
              {filters.dosen.map(d => (
                <option key={d.id} value={d.id}>{d.nama}</option>
              ))}
            </select>
          </div>
          
          <button 
            className="jurnal-dosen__filter-reset"
            onClick={() => {
              setSelectedKategori('');
              setSelectedYear('');
              setSelectedDosen('');
            }}
          >
            Reset Filter
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="jurnal-dosen__stats">
        <span>Menampilkan <strong>{journals.length}</strong> dari <strong>{pagination.total}</strong> jurnal</span>
      </div>

      {/* Journal List */}
      {loading ? (
        <div className="jurnal-dosen__loading">
          <div className="jurnal-dosen__spinner"></div>
          <p>Memuat jurnal...</p>
        </div>
      ) : journals.length === 0 ? (
        <div className="jurnal-dosen__empty">
          <FiFileText />
          <h3>Tidak ada jurnal ditemukan</h3>
          <p>Coba ubah filter atau kata kunci pencarian</p>
        </div>
      ) : (
        <div className={`jurnal-dosen__list jurnal-dosen__list--${viewMode}`}>
          {journals.map(journal => (
            <div key={journal.id_jurnal} className="jurnal-dosen__card">
              <div className="jurnal-dosen__card-header">
                <div className="jurnal-dosen__card-category">
                  {getKategoriLabel(journal.kategori_jurnal)}
                </div>
                {getStatusBadge(journal)}
              </div>
              
              <h3 className="jurnal-dosen__card-title" onClick={() => openDetailModal(journal)}>
                {journal.judul}
              </h3>
              
              <p className="jurnal-dosen__card-authors">{journal.penulis}</p>
              
              <div className="jurnal-dosen__card-meta">
                <span><FiBookOpen /> {journal.nama_jurnal || 'Jurnal'}</span>
                <span><FiCalendar /> {journal.tahun_terbit}</span>
              </div>
              
              {journal.nama_dosen && (
                <div className="jurnal-dosen__card-dosen">
                  <div className="jurnal-dosen__dosen-avatar">
                    {journal.foto_dosen ? (
                      <img src={`${journal.foto_dosen}`} alt={journal.nama_dosen} />
                    ) : (
                      <FiUser />
                    )}
                  </div>
                  <span>{journal.nama_dosen}</span>
                </div>
              )}
              
              <div className="jurnal-dosen__card-actions">
                <button 
                  className="jurnal-dosen__btn jurnal-dosen__btn--secondary"
                  onClick={() => openDetailModal(journal)}
                >
                  <FiInfo /> Detail
                </button>
                
                {canRequest(journal) ? (
                  <button 
                    className="jurnal-dosen__btn jurnal-dosen__btn--primary"
                    onClick={() => openRequestModal(journal)}
                  >
                    <FiSend /> Request Sitasi
                  </button>
                ) : journal.has_pending_request > 0 ? (
                  <button className="jurnal-dosen__btn jurnal-dosen__btn--disabled" disabled>
                    <FiClock /> Menunggu
                  </button>
                ) : journal.last_request_status === 'approved' ? (
                  <button className="jurnal-dosen__btn jurnal-dosen__btn--success" disabled>
                    <FiCheck /> Disetujui
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="jurnal-dosen__pagination">
          <button 
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Sebelumnya
          </button>
          <span>Halaman {pagination.page} dari {pagination.total_pages}</span>
          <button 
            disabled={pagination.page >= pagination.total_pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Selanjutnya
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedJournal && (
        <div className="jurnal-dosen__modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="jurnal-dosen__modal" onClick={e => e.stopPropagation()}>
            <div className="jurnal-dosen__modal-header">
              <h2><FiFileText /> Detail Jurnal</h2>
              <button onClick={() => setShowDetailModal(false)}><FiX /></button>
            </div>
            
            <div className="jurnal-dosen__modal-body">
              <div className="jurnal-dosen__detail-section">
                <h3>{selectedJournal.judul}</h3>
                <p className="jurnal-dosen__detail-authors">{selectedJournal.penulis}</p>
              </div>
              
              <div className="jurnal-dosen__detail-grid">
                <div className="jurnal-dosen__detail-item">
                  <label>Nama Jurnal</label>
                  <span>{selectedJournal.nama_jurnal || '-'}</span>
                </div>
                <div className="jurnal-dosen__detail-item">
                  <label>Tahun Terbit</label>
                  <span>{selectedJournal.tahun_terbit || '-'}</span>
                </div>
                <div className="jurnal-dosen__detail-item">
                  <label>Volume / Nomor</label>
                  <span>{selectedJournal.volume || '-'} / {selectedJournal.nomor || '-'}</span>
                </div>
                <div className="jurnal-dosen__detail-item">
                  <label>Halaman</label>
                  <span>{selectedJournal.halaman || '-'}</span>
                </div>
                <div className="jurnal-dosen__detail-item">
                  <label>DOI</label>
                  <span>{selectedJournal.doi || '-'}</span>
                </div>
                <div className="jurnal-dosen__detail-item">
                  <label>ISSN</label>
                  <span>{selectedJournal.issn || '-'}</span>
                </div>
              </div>
              
              {selectedJournal.abstrak && (
                <div className="jurnal-dosen__detail-section">
                  <label>Abstrak</label>
                  <p className="jurnal-dosen__detail-abstract">{selectedJournal.abstrak}</p>
                </div>
              )}
              
              <div className="jurnal-dosen__detail-dosen">
                <label>Dosen Pemilik</label>
                <div className="jurnal-dosen__dosen-info">
                  <div className="jurnal-dosen__dosen-avatar jurnal-dosen__dosen-avatar--large">
                    {selectedJournal.foto_dosen ? (
                      <img src={`${selectedJournal.foto_dosen}`} alt={selectedJournal.nama_dosen} />
                    ) : (
                      <FiUser />
                    )}
                  </div>
                  <div>
                    <strong>{selectedJournal.nama_dosen}</strong>
                    {selectedJournal.email_dosen && (
                      <span><FiMail /> {selectedJournal.email_dosen}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="jurnal-dosen__detail-links">
                {selectedJournal.url_jurnal && (
                  <a href={selectedJournal.url_jurnal} target="_blank" rel="noopener noreferrer">
                    <FiLink /> Lihat Jurnal Online
                  </a>
                )}
                {selectedJournal.url_pdf && (
                  <a href={selectedJournal.url_pdf} target="_blank" rel="noopener noreferrer">
                    <FiDownload /> Download PDF
                  </a>
                )}
                {selectedJournal.file_path && (
                  <a href={`/api/${selectedJournal.file_path}`} target="_blank" rel="noopener noreferrer">
                    <FiDownload /> Download File
                  </a>
                )}
              </div>
            </div>
            
            <div className="jurnal-dosen__modal-footer">
              <button 
                className="jurnal-dosen__btn jurnal-dosen__btn--secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Tutup
              </button>
              {canRequest(selectedJournal) && (
                <button 
                  className="jurnal-dosen__btn jurnal-dosen__btn--primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    openRequestModal(selectedJournal);
                  }}
                >
                  <FiSend /> Request Sitasi
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Request Modal */}
      {showRequestModal && selectedJournal && (
        <div className="jurnal-dosen__modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="jurnal-dosen__modal jurnal-dosen__modal--small" onClick={e => e.stopPropagation()}>
            <div className="jurnal-dosen__modal-header">
              <h2><FiSend /> Request Sitasi</h2>
              <button onClick={() => setShowRequestModal(false)}><FiX /></button>
            </div>
            
            <form onSubmit={handleRequestSubmit}>
              <div className="jurnal-dosen__modal-body">
                <div className="jurnal-dosen__request-journal">
                  <FiFileText />
                  <div>
                    <strong>{selectedJournal.judul}</strong>
                    <span>{selectedJournal.nama_dosen}</span>
                  </div>
                </div>
                
                <div className="jurnal-dosen__form-group">
                  <label>Keperluan <span className="required">*</span></label>
                  <select 
                    value={requestForm.keperluan}
                    onChange={(e) => setRequestForm({ ...requestForm, keperluan: e.target.value })}
                    required
                  >
                    <option value="Skripsi">Skripsi</option>
                    <option value="Tesis">Tesis</option>
                    <option value="Disertasi">Disertasi</option>
                    <option value="Jurnal">Publikasi Jurnal</option>
                    <option value="Karya Ilmiah">Karya Ilmiah</option>
                    <option value="Tugas Kuliah">Tugas Kuliah</option>
                    <option value="Penelitian">Penelitian</option>
                    <option value="Lainnya">Lainnya</option>
                  </select>
                </div>
                
                <div className="jurnal-dosen__form-group">
                  <label><FiMessageSquare /> Catatan (opsional)</label>
                  <textarea
                    placeholder="Tuliskan pesan atau keterangan tambahan untuk dosen..."
                    value={requestForm.catatan}
                    onChange={(e) => setRequestForm({ ...requestForm, catatan: e.target.value })}
                    rows={4}
                  />
                </div>
                
                <div className="jurnal-dosen__request-info">
                  <FiInfo />
                  <span>Dosen akan mendapatkan notifikasi dan dapat menyetujui atau menolak request Anda.</span>
                </div>
              </div>
              
              <div className="jurnal-dosen__modal-footer">
                <button 
                  type="button"
                  className="jurnal-dosen__btn jurnal-dosen__btn--secondary"
                  onClick={() => setShowRequestModal(false)}
                >
                  Batal
                </button>
                <button 
                  type="submit"
                  className="jurnal-dosen__btn jurnal-dosen__btn--primary"
                  disabled={submitting}
                >
                  {submitting ? 'Mengirim...' : 'Kirim Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* My Requests Modal */}
      {showMyRequests && (
        <div className="jurnal-dosen__modal-overlay" onClick={() => setShowMyRequests(false)}>
          <div className="jurnal-dosen__modal jurnal-dosen__modal--medium" onClick={e => e.stopPropagation()}>
            <div className="jurnal-dosen__modal-header">
              <h2><FiSend /> Request Sitasi Saya</h2>
              <button onClick={() => setShowMyRequests(false)}><FiX /></button>
            </div>
            
            <div className="jurnal-dosen__modal-body">
              {myRequests.length === 0 ? (
                <div className="jurnal-dosen__empty-requests">
                  <FiSend />
                  <p>Belum ada request sitasi</p>
                </div>
              ) : (
                <div className="jurnal-dosen__requests-list">
                  {myRequests.map(request => (
                    <div key={request.id_request} className="jurnal-dosen__request-item">
                      <div className="jurnal-dosen__request-header">
                        <h4>{request.judul_jurnal}</h4>
                        <span className={`jurnal-dosen__status jurnal-dosen__status--${request.status}`}>
                          {request.status === 'pending' ? 'Menunggu' : 
                           request.status === 'approved' ? 'Disetujui' : 'Ditolak'}
                        </span>
                      </div>
                      <p className="jurnal-dosen__request-dosen">
                        <FiUser /> {request.nama_dosen}
                      </p>
                      <div className="jurnal-dosen__request-meta">
                        <span><strong>Keperluan:</strong> {request.keperluan}</span>
                        <span><FiCalendar /> {new Date(request.tanggal_request).toLocaleDateString('id-ID')}</span>
                      </div>
                      {request.catatan && (
                        <p className="jurnal-dosen__request-note">
                          <FiMessageSquare /> {request.catatan}
                        </p>
                      )}
                      {request.catatan_dosen && (
                        <div className="jurnal-dosen__request-response">
                          <strong>Catatan Dosen:</strong>
                          <p>{request.catatan_dosen}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="jurnal-dosen__modal-footer">
              <button 
                className="jurnal-dosen__btn jurnal-dosen__btn--secondary"
                onClick={() => setShowMyRequests(false)}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JurnalDosen;
